
import Vue from "vue";
import moment from "moment";
import { getAvailableNowTalent } from "@/api";
import EntityIdLink from "@/components/shared/EntityIdLink";
import {
  getFfsStatusColor,
  getFfsStatusLabel,
  ffsStatusNeedsDarkText,
  resolveCanonicalFfsPresentationCode
} from "@/helpers/ffsStatus";

interface AvailableNowRow {
  id: number;
  firstName: string;
  lastName: string;
  type: number;
  status: number;
  currentStatusCode?: string | null;
  city: string | null;
  province: string | null;
  country: number | null;
  availableNowSince: string | null;
  email: string | null;
}

export default Vue.extend({
  name: "AvailableNow",
  components: { EntityIdLink },
  data() {
    return {
      loading: false,
      talents: [] as AvailableNowRow[],
      roleFilter: null as number | null,
      provinceFilter: null as string | null,
      roleFilterOptions: [
        { label: "Hygienist", value: 0 },
        { label: "Dental Assistant", value: 1 }
      ],
      headers: [
        { text: "Talent ID", value: "id", width: "110px" },
        { text: "Name", value: "name", sortable: false },
        { text: "Role", value: "type", width: "140px" },
        { text: "Status", value: "status", width: "110px" },
        { text: "City", value: "city" },
        { text: "Province / State", value: "province", width: "140px" },
        { text: "Country", value: "country", width: "130px" },
        {
          text: "Available-Now Since",
          value: "availableNowSince",
          width: "240px"
        }
      ]
    };
  },
  computed: {
    provinceOptions(): string[] {
      const provinces = this.talents
        .map(t => t.province)
        .filter((p): p is string => !!p);
      return [...new Set(provinces)].sort();
    },
    filteredTalents(): AvailableNowRow[] {
      return this.talents.filter(t => {
        if (this.roleFilter !== null && t.type !== this.roleFilter) {
          return false;
        }
        if (this.provinceFilter && t.province !== this.provinceFilter) {
          return false;
        }
        return true;
      });
    }
  },
  methods: {
    roleLabel(type: number): string {
      return type === 1 ? "Dental Assistant" : "Hygienist";
    },
    // Membership presentation is canonical-only; unavailable data stays neutral.
    rowStatusCode(item: AvailableNowRow): string {
      return resolveCanonicalFfsPresentationCode(item?.currentStatusCode) || "";
    },
    getStatusColor(code: string): string {
      return getFfsStatusColor(code);
    },
    getStatusLabel(code: string): string {
      return getFfsStatusLabel(code);
    },
    statusNeedsDarkText(code: string): boolean {
      return ffsStatusNeedsDarkText(code);
    },
    formatSince(value: string): string {
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
    },
    timeAgo(value: string): string {
      const parsed = moment(value);
      return parsed.isValid() ? parsed.fromNow() : "";
    },
    async fetchList() {
      this.loading = true;
      try {
        this.talents = await getAvailableNowTalent();
      } catch (error) {
        console.error("Failed to load Available Now list", error);
        this.talents = [];
        this.$store.commit("callSnackbar", {
          visible: true,
          text: "Could not load the Available Now list",
          color: "#FB7672"
        });
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    this.fetchList();
  }
});
